<script lang="ts">
  import { CheckCircle, XCircle, RotateCcw } from 'lucide-svelte';
  import { scale, fade } from 'svelte/transition';
  import Button from '../ui/button.svelte';
  import type { ExerciseItem, EvaluationResult, UserAnswer, WordBuildAnswer } from '../../types/exercise';
  import { _ } from 'svelte-i18n';

  interface Props {
    exercise: ExerciseItem;
    showFeedback: boolean;
    evaluationResult: EvaluationResult | null;
    onAnswerChange: (answer: UserAnswer) => void;
  }

  let { exercise, showFeedback, evaluationResult, onAnswerChange }: Props = $props();

  // Local state
  let builtWord = $state<string[]>([]);
  let availableLetters = $state<{ letter: string; index: number; used: boolean }[]>([]);

  // Get mode-specific data
  let modeData = $derived(
    exercise.modeData.type === 'WORD_BUILD' ? exercise.modeData : null
  );

  // Built word as string
  let builtWordStr = $derived(builtWord.join(''));

  // Reset state when exercise changes
  $effect(() => {
    if (exercise.id && modeData) {
      builtWord = [];
      availableLetters = modeData.shuffledLetters.map((letter, index) => ({
        letter,
        index,
        used: false,
      }));
    }
  });

  // Update parent when built word changes
  $effect(() => {
    if (builtWord.length > 0) {
      const answer: WordBuildAnswer = {
        type: 'WORD_BUILD',
        builtWord: builtWordStr,
      };
      onAnswerChange(answer);
    }
  });

  // Add letter to built word
  function addLetter(letterObj: { letter: string; index: number; used: boolean }) {
    if (showFeedback || letterObj.used) return;

    builtWord = [...builtWord, letterObj.letter];
    availableLetters = availableLetters.map((l) =>
      l.index === letterObj.index ? { ...l, used: true } : l
    );
  }

  // Remove last letter from built word
  function removeLast() {
    if (showFeedback || builtWord.length === 0) return;

    const lastLetter = builtWord[builtWord.length - 1];
    builtWord = builtWord.slice(0, -1);

    // Find the first used letter matching and mark it unused
    const usedIndex = availableLetters.findIndex(
      (l) => l.used && l.letter === lastLetter
    );
    if (usedIndex !== -1) {
      availableLetters = availableLetters.map((l, i) =>
        i === usedIndex ? { ...l, used: false } : l
      );
    }
  }

  // Reset built word
  function resetWord() {
    if (showFeedback) return;

    builtWord = [];
    availableLetters = availableLetters.map((l) => ({ ...l, used: false }));
  }

  // Check if word is complete
  let isComplete = $derived(
    modeData ? builtWord.length === modeData.targetWord.length : false
  );
</script>

<div class="space-y-8">
  <!-- Prompt -->
  <div class="text-center" in:scale={{ duration: 300, start: 0.95 }}>
    <h2 class="text-lg font-semibold text-muted-foreground mb-2">
      {$_('exercise.build_word_from_letters')}
    </h2>
    <p class="text-3xl font-bold tracking-tight mb-2">{exercise.prompt}</p>
  </div>

  <!-- Built word display -->
  <div class="flex justify-center">
    <div
      class="flex gap-2 min-h-[80px] p-4 rounded-2xl border-2 border-dashed items-center justify-center flex-wrap min-w-[200px] transition-all duration-300
             {showFeedback
               ? evaluationResult?.isCorrect
                 ? 'border-green-500 bg-green-50 dark:bg-green-500/30'
                 : 'border-red-500 bg-red-50 dark:bg-red-500/30'
               : 'border-primary/30 bg-secondary/10'}"
    >
      {#if builtWord.length > 0}
        {#each builtWord as letter, i}
          <div
            in:scale={{ duration: 200 }}
            class="w-12 h-12 flex items-center justify-center rounded-xl text-2xl font-bold shadow-md border-2
                   {showFeedback
                     ? evaluationResult?.isCorrect
                       ? 'bg-white dark:bg-green-900 text-green-900 dark:text-green-100 border-green-600 dark:border-green-700'
                       : 'bg-white dark:bg-red-900 text-red-900 dark:text-red-100 border-red-600 dark:border-red-700'
                     : 'bg-primary text-primary-foreground border-primary-foreground/20'}"
          >
            {letter === ' ' ? '␣' : letter}
          </div>
        {/each}
      {:else}
        <div class="flex items-center text-muted-foreground/60 px-4 font-medium italic">
          {$_('exercise.click_letters_below')}
        </div>
      {/if}
    </div>
  </div>

  <!-- Action buttons -->
  {#if !showFeedback}
    <div class="flex justify-center gap-3">
      <Button
        onclick={removeLast}
        variant="outline"
        size="sm"
        disabled={builtWord.length === 0}
        class="h-9 px-4 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
      >
        {$_('exercise.back')}
      </Button>
      <Button
        onclick={resetWord}
        variant="outline"
        size="sm"
        disabled={builtWord.length === 0}
        class="h-9 px-4"
      >
        <RotateCcw class="w-4 h-4 mr-1" />
        {$_('exercise.reset')}
      </Button>
    </div>
  {/if}

  <!-- Available letters -->
  <div class="flex flex-wrap justify-center gap-3 py-4">
    {#each availableLetters as letterObj}
      <button
        type="button"
        onclick={() => addLetter(letterObj)}
        disabled={showFeedback || letterObj.used}
        class="w-14 h-14 flex items-center justify-center rounded-2xl text-2xl font-bold
               transition-all duration-200 border-b-4 active:border-b-0 active:translate-y-1
               {letterObj.used
                 ? 'bg-muted text-muted-foreground opacity-30 scale-90 border-transparent cursor-default'
                 : 'bg-card hover:bg-accent border-border hover:border-primary/50 text-foreground shadow-sm cursor-pointer hover:scale-105'}"
      >
        {letterObj.letter === ' ' ? '␣' : letterObj.letter}
      </button>
    {/each}
  </div>

  <!-- Status indicator -->
  {#if !showFeedback && modeData}
    <div class="text-center text-sm text-muted-foreground">
      <span class="font-mono bg-secondary/50 px-2 py-0.5 rounded text-xs">{builtWord.length} / {modeData.targetWord.length}</span>
      {#if isComplete}
        <span in:fade class="text-primary ml-2 font-semibold">{$_('exercise.ready_for_check')}</span>
      {/if}
    </div>
  {/if}

  <!-- Feedback -->
  {#if showFeedback && evaluationResult}
    <div
      in:scale={{ duration: 300, start: 0.95 }}
      class="p-5 rounded-xl shadow-lg border-l-4 backdrop-blur-sm
        {evaluationResult.isCorrect
        ? 'bg-green-100/80 dark:bg-green-500/30 border-green-500 text-green-900 dark:text-green-50'
        : 'bg-red-100/80 dark:bg-red-500/30 border-red-500 text-red-900 dark:text-red-50'}"
    >
      <div class="flex items-center gap-3 mb-2">
        {#if evaluationResult.isCorrect}
            <div class="p-1 bg-green-200 dark:bg-green-800 rounded-full">
                <CheckCircle class="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <span class="font-bold text-lg">{$_('exercise.correct')}</span>
        {:else}
            <div class="p-1 bg-red-200 dark:bg-red-800 rounded-full">
                <XCircle class="w-6 h-6 text-red-700 dark:text-red-300" />
            </div>
            <span class="font-bold text-lg">{$_('exercise.incorrect')}</span>
        {/if}
      </div>

      {#if !evaluationResult.isCorrect && modeData}
        <div class="ml-11">
            <p class="text-sm opacity-90">
            {$_('exercise.correct_answer_label')} <strong class="text-lg ml-1">{modeData.targetWord}</strong>
            </p>
        </div>
      {/if}
    </div>
  {/if}
</div>
